import { useState } from 'react';
import { motion } from 'framer-motion';
import { Shield, FileText, Lock, Sparkles, Check } from 'lucide-react';
import { Mascot } from '@/components/Mascot';
import { Button } from '@/components/Button';
import { useTelegram } from '@/hooks/useTelegram';
import { useLegalAcceptance } from '@/hooks/useLegalAcceptance';
import { LegalDocumentViewer } from '@/components/LegalDocumentViewer';
import { LEGAL_DOCUMENTS, LEGAL_DOCUMENT_VERSION, type LegalDocument } from '@/legal';

/**
 * Экран принятия юридических документов
 * 
 * Показывается:
 * - Новым юзерам при первом запуске
 * - Существующим юзерам когда обновилась редакция документов
 */
export function LegalAcceptScreen() {
  const { haptic } = useTelegram();
  const { acceptDocuments, accepting, isExistingUser } = useLegalAcceptance();
  const [agreed, setAgreed] = useState(false);
  const [viewingDoc, setViewingDoc] = useState<LegalDocument | null>(null);

  function toggleAgreed() {
    haptic('light');
    setAgreed((v) => !v);
  }

  function openDoc(doc: LegalDocument) {
    haptic('light');
    setViewingDoc(doc);
  }

  async function handleAccept() {
    if (!agreed || accepting) return;
    haptic('medium');
    const ok = await acceptDocuments();
    if (ok) {
      haptic('success');
    } else {
      haptic('error');
      alert('Не удалось сохранить. Попробуй ещё раз через минуту.');
    }
  }

  return (
    <>
      <div className="min-h-screen bg-bg flex flex-col max-w-md mx-auto">
        {/* Header */}
        <div className="px-5 pt-8 pb-4">
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center justify-center mb-4"
          >
            {/* Новичка после заставки встречает тот же Дофи, а не щит «ответственности» */}
            {isExistingUser ? (
              <div className="w-16 h-16 rounded-2xl bg-emerald-glow/20 flex items-center justify-center">
                <Shield size={32} className="text-emerald-glow" />
              </div>
            ) : (
              <Mascot pose="calm" className="w-20 h-20" />
            )}
          </motion.div>
          
          <motion.h1
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="font-display font-bold text-2xl text-center mb-2"
          >
            {isExistingUser ? 'Обновили условия' : 'Одна галочка — и начинаем'}
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="text-sm text-ink-muted text-center leading-relaxed"
          >
            {isExistingUser 
              ? 'Мы обновили юридические документы. Прочитай и прими новую редакцию, чтобы продолжить.'
              : 'Коротко о главном ниже. Документы можно открыть и прочитать целиком.'}
          </motion.p>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto px-5 pb-32">
          {/* Документы для просмотра */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="space-y-2 mb-5"
          >
            <DocumentCard
              icon={<FileText size={18} className="text-emerald-glow" />}
              title={LEGAL_DOCUMENTS['oferta'].title}
              description={LEGAL_DOCUMENTS['oferta'].description}
              onClick={() => openDoc(LEGAL_DOCUMENTS['oferta'])}
            />
            <DocumentCard
              icon={<Lock size={18} className="text-sky-glow" />}
              title={LEGAL_DOCUMENTS['privacy'].title}
              description={LEGAL_DOCUMENTS['privacy'].description}
              onClick={() => openDoc(LEGAL_DOCUMENTS['privacy'])}
            />
            <DocumentCard
              icon={<Sparkles size={18} className="text-warn" />}
              title={LEGAL_DOCUMENTS['consent-pd'].title}
              description={LEGAL_DOCUMENTS['consent-pd'].description}
              onClick={() => openDoc(LEGAL_DOCUMENTS['consent-pd'])}
            />
          </motion.div>

          {/* Важная информация — короткое summary */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="card-elevated p-4 mb-5"
          >
            <div className="text-xs text-ink-muted leading-relaxed space-y-2">
              <p className="text-ink font-semibold mb-2">Коротко:</p>
              <p>• Slava Health — <strong className="text-ink">информационный сервис</strong>, а не медицинская услуга.</p>
              <p>• AI-помощник «Дофи» может ошибаться. Он <strong className="text-ink">не специалист</strong> — не врач, не психолог, не диетолог.</p>
              <p>• Решения о здоровье — <strong className="text-ink">твоя ответственность</strong>. При вопросах обращайся к врачу.</p>
              <p>• Сервис для лиц <strong className="text-ink">18+</strong>.</p>
            </div>
          </motion.div>

          {/* Главная галочка */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            <button
              type="button"
              onClick={toggleAgreed}
              className={`w-full text-left p-4 rounded-2xl border-2 transition-all ${
                agreed
                  ? 'bg-emerald-glow/10 border-emerald-glow/50'
                  : 'bg-bg-card border-line hover:border-emerald-glow/30'
              }`}
            >
              <div className="flex items-start gap-3">
                <div className={`w-6 h-6 rounded-md shrink-0 mt-0.5 flex items-center justify-center transition-all ${
                  agreed
                    ? 'bg-emerald-glow text-bg'
                    : 'bg-bg-elevated border border-line'
                }`}>
                  {agreed && <Check size={16} strokeWidth={3} />}
                </div>
                <div className="text-xs text-ink leading-relaxed">
                  Я ознакомлен(а) с указанными выше документами, принимаю их условия, даю согласие на обработку моих персональных данных (включая данные о состоянии здоровья) и подтверждаю, что мне исполнилось <strong>18 лет</strong>.
                </div>
              </div>
            </button>
          </motion.div>
        </div>

        {/* Sticky button */}
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.6, type: 'spring', stiffness: 400, damping: 30 }}
          className="fixed bottom-0 left-0 right-0 px-5 pb-5 pt-3 bg-gradient-to-t from-bg via-bg to-bg/0 max-w-md mx-auto"
        >
          <Button
            fullWidth
            size="lg"
            disabled={!agreed || accepting}
            onClick={handleAccept}
            className="shadow-2xl"
          >
            {accepting ? 'Сохраняем...' : 'Принять и продолжить'}
          </Button>
          <p className="text-[10px] text-ink-dim text-center mt-2">
            Версия документов: {LEGAL_DOCUMENT_VERSION}
          </p>
        </motion.div>
      </div>

      <LegalDocumentViewer
        document={viewingDoc}
        onClose={() => setViewingDoc(null)}
      />
    </>
  );
}

interface DocumentCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  onClick: () => void;
}

function DocumentCard({ icon, title, description, onClick }: DocumentCardProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full p-3 rounded-xl bg-bg-card border border-line hover:border-emerald-glow/30 hover:bg-emerald-glow/5 active:scale-[0.99] transition-all flex items-center gap-3 text-left"
    >
      <div className="w-9 h-9 rounded-lg bg-bg-elevated flex items-center justify-center shrink-0">
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-display font-semibold text-sm mb-0.5">{title}</div>
        <div className="text-[11px] text-ink-muted leading-tight">{description}</div>
      </div>
      <div className="text-emerald-glow text-xs font-medium shrink-0">
        Читать →
      </div>
    </button>
  );
}
